<template>
    <div class="page points-page">
        <div class="page-header">
            <h1>測驗管理</h1>
            <p>查看並管理所有的測驗</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <div class="filters">
                    <a-space wrap size="middle">
                        <a-space>
                            <span>模式：</span>
                            <a-select
                                ref="mode_select"
                                v-model:value="modeValue"
                                size="middle"
                                class="status-select min-w-[120px]"
                                :options="modeOptions"
                                allow-clear
                            ></a-select>
                        </a-space>

                        <a-space>
                            <span>狀態：</span>
                            <a-select
                                ref="status_select"
                                v-model:value="statusValue"
                                size="middle"
                                class="status-select min-w-[120px]"
                                :options="statusOptions"
                                allow-clear
                            ></a-select>
                        </a-space>

                        <div class="search-box">
                            <a-input
                                v-model:value="searchText"
                                placeholder="搜尋測驗名稱"
                                size="middle"
                                class="search-input-field min-w-[300px]"
                                allow-clear
                                @press-enter="handleSearch"
                            />
                        </div>
                    </a-space>
                </div>

                <div class="toolbar-actions">
                    <a-space>
                        <a-button
                            v-if="canCreate"
                            type="primary"
                            size="middle"
                            class="unified-add-btn"
                            @click="goCreate"
                        >
                            新增測驗
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 測驗列表表格 -->
            <DataTable
                :showIndex="false"
                title=""
                :columns="columns"
                :data-source="quizList"
                :pagination="pagination"
                :loading="loading"
                @refresh="loadQuizzes"
                @change="handleTableChange"
                class="points-table"
            >
                <template #titleActions>
                    <a-tooltip title="欄位設置">
                        <a-button
                            type="text"
                            class="column-settings-icon-btn"
                            @click="showColumnSettingsModal"
                        >
                            <template #icon>
                                <TableOutlined />
                            </template>
                        </a-button>
                    </a-tooltip>
                </template>
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'name'">
                        <a-flex justify="center" align="center">
                            <b
                                class="quiz_answer_mask"
                                v-if="record.has_answers"
                            >
                                解
                            </b>
                            <a-typography-link
                                v-if="canView"
                                class="quiz-name-link"
                                @click="goView(record.id)"
                            >
                                {{ record.name }}
                            </a-typography-link>
                            <span v-else>{{ record.name }}</span>
                        </a-flex>
                    </template>

                    <template v-else-if="column.key === 'description'">
                        <a-tooltip
                            v-if="record.description"
                            :title="record.description"
                        >
                            <div class="quiz-description-cell">
                                {{ record.description }}
                            </div>
                        </a-tooltip>
                        <span v-else>-</span>
                    </template>

                    <template v-else-if="column.key === 'scoring_mode_display'">
                        <a-tag
                            style="line-height: initial"
                            class="scoring-mode-tag"
                            :color="getScoringModeColor(record.scoring_mode)"
                        >
                            {{ record.scoring_mode_display }}
                        </a-tag>
                    </template>

                    <template v-else-if="column.key === 'status_display'">
                        <a-tag
                            style="line-height: initial"
                            :color="
                                record.status === 'draft'
                                    ? 'orange'
                                    : record.status === 'published'
                                      ? 'purple'
                                      : 'blue'
                            "
                        >
                            {{ record.status_display }}
                        </a-tag>
                    </template>

                    <template v-else-if="column.key === 'passing_score'">
                        {{ record.passing_score ? record.passing_score : '-' }}
                    </template>

                    <template v-else-if="column.key === 'completed_or_not'">
                        {{
                            record.completed_attempts
                                ? record.completed_attempts
                                : '0'
                        }}
                        /
                        {{
                            record.total_attempts ? record.total_attempts : '0'
                        }}
                    </template>

                    <template v-else-if="column.key === 'do_by_name'">
                        <div class="two-line-cell">
                            <div class="cell-line">
                                {{
                                    record.updated_by ? record.updated_by : '-'
                                }}
                            </div>
                            <div class="cell-line">
                                {{
                                    record.created_by ? record.created_by : '-'
                                }}
                            </div>
                        </div>
                    </template>

                    <template v-else-if="column.key === 'do_by_time'">
                        <div class="two-line-cell">
                            <div class="cell-line">
                                {{
                                    record.updated_at ? record.updated_at : '-'
                                }}
                            </div>
                            <div class="cell-line">
                                {{
                                    record.created_at ? record.created_at : '-'
                                }}
                            </div>
                        </div>
                    </template>

                    <template v-else-if="column.key === 'actions'">
                        <template v-if="hasAnyAction(record)">
                            <a-space class="action-buttons" size="small" wrap>
                                <a-button
                                    v-if="
                                        canEdit && record.status !== 'published'
                                    "
                                    size="small"
                                    class="action-btn-edit"
                                    @click="goEdit(record.id)"
                                >
                                    修改
                                </a-button>
                                <a-button
                                    v-if="
                                        canEdit &&
                                        record.status !== 'published' &&
                                        record.scoring_mode !== 'survey'
                                    "
                                    size="small"
                                    class="action-btn-edit"
                                    @click="goSetAnswers(record.id)"
                                >
                                    設定
                                </a-button>
                                <a-button
                                    v-if="
                                        canEdit &&
                                        (record.status === 'draft' ||
                                            record.status === 'archived') &&
                                        (record.scoring_mode === 'survey' ||
                                            record.has_answers === true)
                                    "
                                    size="small"
                                    class="action-btn-publish"
                                    @click="confirmPublish(record)"
                                >
                                    發布
                                </a-button>
                                <a-button
                                    v-if="
                                        canEdit &&
                                        (record.status === 'draft' ||
                                            record.status === 'published')
                                    "
                                    size="small"
                                    class="action-btn-archive"
                                    @click="confirmArchive(record)"
                                >
                                    封存
                                </a-button>
                                <a-button
                                    v-if="
                                        canDelete &&
                                        record.status === 'archived'
                                    "
                                    size="small"
                                    class="action-btn-delete"
                                    @click="confirmDelete(record)"
                                >
                                    刪除
                                </a-button>
                            </a-space>
                        </template>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 發布測驗 Modal -->
        <a-modal
            v-model:open="publishModalVisible"
            title="發布測驗"
            :width="600"
            :confirm-loading="publishLoading"
            @ok="handleConfirmPublish"
            @cancel="publishModalVisible = false"
        >
            <div v-if="publishQuizData" class="quiz-modal-content">
                <!-- 測驗資訊 -->
                <div class="quiz-info">
                    <h3 class="quiz-section-title">測驗資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="測驗名稱">
                            <span class="quiz-name-display">
                                {{ publishQuizData.name }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="測驗狀態">
                            <a-tag
                                :color="getStatusColor(publishQuizData.status)"
                            >
                                {{
                                    publishQuizData.status_display ||
                                    publishQuizData.status
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="測驗模式">
                            <a-tag
                                class="scoring-mode-tag"
                                :color="
                                    getScoringModeColor(
                                        publishQuizData.scoring_mode
                                    )
                                "
                            >
                                {{
                                    publishQuizData.scoring_mode_display ||
                                    publishQuizData.scoring_mode
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="publishQuizData.time_limit"
                            label="時間限制"
                        >
                            {{
                                publishQuizData.time_limit_display ||
                                publishQuizData.time_limit
                            }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="publishQuizData.max_attempts_per_user"
                            label="作答次數"
                        >
                            {{ publishQuizData.max_attempts_per_user }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="publishQuizData.description"
                            label="說明"
                            :span="2"
                        >
                            {{ publishQuizData.description }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
            </div>
            <template #footer>
                <a-button @click="publishModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    :loading="publishLoading"
                    @click="handleConfirmPublish"
                >
                    確認發布
                </a-button>
            </template>
        </a-modal>

        <!-- 封存測驗 Modal -->
        <a-modal
            v-model:open="archiveModalVisible"
            title="封存測驗"
            :width="600"
            :confirm-loading="archiveLoading"
            @ok="handleConfirmArchive"
            @cancel="archiveModalVisible = false"
        >
            <div v-if="archiveQuizData" class="quiz-modal-content">
                <!-- 測驗資訊 -->
                <div class="quiz-info">
                    <h3 class="quiz-section-title">測驗資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="測驗名稱">
                            <span class="quiz-name-display">
                                {{ archiveQuizData.name }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="測驗狀態">
                            <a-tag
                                :color="getStatusColor(archiveQuizData.status)"
                            >
                                {{
                                    archiveQuizData.status_display ||
                                    archiveQuizData.status
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="測驗模式">
                            <a-tag
                                class="scoring-mode-tag"
                                :color="
                                    getScoringModeColor(
                                        archiveQuizData.scoring_mode
                                    )
                                "
                            >
                                {{
                                    archiveQuizData.scoring_mode_display ||
                                    archiveQuizData.scoring_mode
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="archiveQuizData.time_limit"
                            label="時間限制"
                        >
                            {{
                                archiveQuizData.time_limit_display ||
                                archiveQuizData.time_limit
                            }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="archiveQuizData.max_attempts_per_user"
                            label="作答次數"
                        >
                            {{ archiveQuizData.max_attempts_per_user }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="archiveQuizData.description"
                            label="說明"
                            :span="2"
                        >
                            {{ archiveQuizData.description }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
            </div>
            <template #footer>
                <a-button @click="archiveModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    :loading="archiveLoading"
                    @click="handleConfirmArchive"
                >
                    確認封存
                </a-button>
            </template>
        </a-modal>

        <!-- 刪除測驗 Modal -->
        <a-modal
            v-model:open="deleteModalVisible"
            title="刪除測驗"
            :width="600"
            :confirm-loading="deleteLoading"
            @ok="handleConfirmDelete"
            @cancel="deleteModalVisible = false"
        >
            <div v-if="deleteQuizData" class="quiz-modal-content">
                <!-- 測驗資訊 -->
                <div class="quiz-info">
                    <h3 class="quiz-section-title">測驗資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="測驗名稱">
                            <span class="quiz-name-display">
                                {{ deleteQuizData.name }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="測驗狀態">
                            <a-tag
                                :color="getStatusColor(deleteQuizData.status)"
                            >
                                {{
                                    deleteQuizData.status_display ||
                                    deleteQuizData.status
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="測驗模式">
                            <a-tag
                                class="scoring-mode-tag"
                                :color="
                                    getScoringModeColor(
                                        deleteQuizData.scoring_mode
                                    )
                                "
                            >
                                {{
                                    deleteQuizData.scoring_mode_display ||
                                    deleteQuizData.scoring_mode
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteQuizData.time_limit"
                            label="時間限制"
                        >
                            {{
                                deleteQuizData.time_limit_display ||
                                deleteQuizData.time_limit
                            }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteQuizData.max_attempts_per_user"
                            label="作答次數"
                        >
                            {{ deleteQuizData.max_attempts_per_user }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteQuizData.description"
                            label="說明"
                            :span="2"
                        >
                            {{ deleteQuizData.description }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 操作提示 -->
                <div class="quiz-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作無法復原，請確認後再執行"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="deleteModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    danger
                    :loading="deleteLoading"
                    @click="handleConfirmDelete"
                >
                    確認刪除
                </a-button>
            </template>
        </a-modal>

        <!-- 欄位設置 Modal -->
        <a-modal
            v-model:open="columnSettingsModalVisible"
            title="欄位顯示設置"
            width="500px"
        >
            <div class="column-settings-content">
                <p class="column-settings-hint">
                    選擇要在表格中顯示的欄位（預設全部顯示）
                </p>
                <a-checkbox-group
                    v-model:value="visibleColumns"
                    class="column-checkbox-group"
                >
                    <a-row :gutter="[16, 12]">
                        <a-col
                            :span="12"
                            v-for="col in allColumns"
                            :key="col.key"
                        >
                            <a-checkbox
                                :value="col.key"
                                :disabled="col.key === 'actions'"
                            >
                                {{ col.title }}
                                <span
                                    v-if="col.key === 'actions'"
                                    class="required-badge"
                                >
                                    （必選）
                                </span>
                            </a-checkbox>
                        </a-col>
                    </a-row>
                </a-checkbox-group>
            </div>
            <template #footer>
                <a-button @click="columnSettingsModalVisible = false">
                    取消
                </a-button>
                <a-button type="primary" @click="saveColumnSettings">
                    確定
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { TableOutlined } from '@ant-design/icons-vue';
import DataTable from '@/components/common/DataTable.vue';
import {
    getQuizzes,
    getSingleQuiz,
    updateQuizzes,
    deleteQuizzes
} from '@/api/quiz';
import { useRouter } from 'vue-router';
import { useAdminInfo } from '@/stores/useAdminInfo';

const router = useRouter();
const adminStore = useAdminInfo();

// 權限檢查
const canCreate = computed(() => adminStore.hasPermissionCode('140101')); // 新增測驗
const canEdit = computed(() => adminStore.hasPermissionCode('140102')); // 修改測驗
const canDelete = computed(() => adminStore.hasPermissionCode('140103')); // 刪除測驗
const canView = computed(() => adminStore.hasPermissionCode('140104')); // 查看測驗

// 檢查記錄是否有任何可見的操作按鈕
const hasAnyAction = (record) => {
    // 修改按鈕
    if (canEdit.value && record.status !== 'published') return true;
    // 設定按鈕
    if (
        canEdit.value &&
        record.status !== 'published' &&
        record.scoring_mode !== 'survey'
    )
        return true;
    // 發布按鈕
    if (
        canEdit.value &&
        (record.status === 'draft' || record.status === 'archived') &&
        (record.scoring_mode === 'survey' || record.has_answers === true)
    )
        return true;
    // 封存按鈕
    if (
        canEdit.value &&
        (record.status === 'draft' || record.status === 'published')
    )
        return true;
    // 刪除按鈕
    if (canDelete.value && record.status === 'archived') return true;
    return false;
};
const loading = ref(false);
const searchText = ref('');
const quizList = ref([]);
const modeValue = ref('');
const statusValue = ref('');

// Modal 狀態
const publishModalVisible = ref(false);
const publishQuizData = ref(null);
const publishLoading = ref(false);

const archiveModalVisible = ref(false);
const archiveQuizData = ref(null);
const archiveLoading = ref(false);

const deleteModalVisible = ref(false);
const deleteQuizData = ref(null);
const deleteLoading = ref(false);

// 欄位設置
const STORAGE_KEY = 'quizzes_management_columns';
const columnSettingsModalVisible = ref(false);

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 筆`
});

// 檢查是否有任何操作權限（用於決定是否顯示操作欄）
const hasAnyOperationPermission = computed(() => {
    return canEdit.value || canDelete.value;
});

// 所有可用欄位（定義為函數，避免 computed 依賴問題）
const getAllColumns = () => {
    const baseColumns = [
        {
            title: '#',
            key: 'id',
            dataIndex: 'id',
            width: 100,
            align: 'center'
        },
        {
            title: '名稱',
            key: 'name',
            dataIndex: 'name',
            width: 150,
            align: 'center'
        },
        {
            title: '模式',
            key: 'scoring_mode_display',
            dataIndex: 'scoring_mode_display',
            width: 120,
            align: 'center'
        },
        {
            title: '狀態',
            key: 'status_display',
            dataIndex: 'status_display',
            width: 120,
            align: 'center'
        },
        {
            title: '說明',
            key: 'description',
            dataIndex: 'description',
            width: 200,
            align: 'center'
        },
        {
            title: '時間限制',
            key: 'time_limit_display',
            dataIndex: 'time_limit_display',
            width: 120,
            align: 'center'
        },
        {
            title: '作答次數',
            key: 'max_attempts_per_user',
            dataIndex: 'max_attempts_per_user',
            width: 200,
            align: 'center'
        },
        {
            title: '及格分數',
            key: 'passing_score',
            dataIndex: 'passing_score',
            width: 150,
            align: 'center'
        },
        {
            title: '完成/總計',
            key: 'completed_or_not',
            width: 150,
            align: 'center'
        },
        {
            title: '編輯者/建立者',
            key: 'do_by_name',
            width: 180,
            align: 'left'
        },
        {
            title: '更新時間/創建時間',
            key: 'do_by_time',
            width: 180,
            align: 'left'
        }
    ];

    // 只有當有操作權限時才添加操作欄
    if (hasAnyOperationPermission.value) {
        baseColumns.push({
            title: '操作',
            key: 'actions',
            align: 'center',
            width: 200,
            fixed: 'right'
        });
    }

    return baseColumns;
};

// 所有可用欄位（computed）
const allColumns = computed(() => {
    return getAllColumns();
});

// 初始化可見欄位
const initVisibleColumns = () => {
    const cols = getAllColumns();
    visibleColumns.value = cols.map((col) => col.key);
};

// 預設可見欄位
const visibleColumns = ref([]);

// 載入保存的欄位設置
const loadColumnSettings = () => {
    try {
        // 先初始化預設值
        if (visibleColumns.value.length === 0) {
            initVisibleColumns();
        }

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const savedColumns = JSON.parse(saved);
            const allColKeys = getAllColumns().map((col) => col.key);
            // 驗證保存的欄位是否還存在於 allColumns 中
            const validColumns = savedColumns.filter((key) =>
                allColKeys.includes(key)
            );
            if (validColumns.length > 0) {
                visibleColumns.value = validColumns;
                // 確保「操作」欄位始終包含在內
                if (
                    hasAnyOperationPermission.value &&
                    !visibleColumns.value.includes('actions')
                ) {
                    visibleColumns.value.push('actions');
                }
            } else {
                // 如果保存的欄位都無效，使用預設值
                initVisibleColumns();
            }
        } else {
            // 如果沒有保存的設置，使用預設值
            initVisibleColumns();
        }
        // 確保「操作」欄位始終包含在內
        if (
            hasAnyOperationPermission.value &&
            !visibleColumns.value.includes('actions')
        ) {
            visibleColumns.value.push('actions');
        }
    } catch (error) {
        console.error('載入欄位設置失敗:', error);
        // 發生錯誤時，使用預設值
        initVisibleColumns();
    }
};

// 保存欄位設置到 localStorage
const saveColumnSettings = () => {
    try {
        // 確保「操作」欄位始終包含在內
        if (
            hasAnyOperationPermission.value &&
            !visibleColumns.value.includes('actions')
        ) {
            visibleColumns.value.push('actions');
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns.value));
        columnSettingsModalVisible.value = false;
        message.success('欄位設置已保存');
    } catch (error) {
        console.error('保存欄位設置失敗:', error);
        showErrorMessage('保存欄位設置失敗');
    }
};

// 顯示欄位設置模態框
const showColumnSettingsModal = () => {
    columnSettingsModalVisible.value = true;
};

// 表格欄位配置 - 根據 visibleColumns 過濾
const columns = computed(() => {
    return allColumns.value.filter((col) =>
        visibleColumns.value.includes(col.key)
    );
});

const modeOptions = [
    { label: '全部', value: '' },
    { label: '測驗', value: 'normal' },
    { label: '維度', value: 'dimensions' },
    { label: '問卷', value: 'survey' }
];

const statusOptions = [
    { label: '全部', value: '' },
    { label: '草稿', value: 'draft' },
    { label: '已發布', value: 'published' },
    { label: '已封存', value: 'archived' }
];

// 載入測驗列表
const loadQuizzes = async () => {
    try {
        loading.value = true;

        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        searchText.value ? (params['name'] = searchText.value) : null;
        statusValue.value ? (params['status'] = statusValue.value) : null;
        modeValue.value ? (params['mode'] = modeValue.value) : null;

        const response = await getQuizzes(params);

        if (response.result) {
            quizList.value = response.data.data || [];
            pagination.total = response.data.pagination.total || 0;
            pagination.current = response.data.pagination.current_page || 1;
        } else {
            showErrorMessage(response.message || '載入失敗');
        }
    } catch (error) {
        showErrorMessage('載入測驗清單失敗');
    } finally {
        loading.value = false;
    }
};

// 處理表格變化
const handleTableChange = (pag, filters, sorter) => {
    const { pagination: p } = pag;
    pagination.current = p.current;
    pagination.pageSize = p.pageSize;
    loadQuizzes();
};

// 搜尋
const handleSearch = () => {
    pagination.current = 1;
    loadQuizzes();
};

// 重置篩選條件
const handleReset = () => {
    modeValue.value = '';
    statusValue.value = '';
    searchText.value = '';
    pagination.current = 1;
    loadQuizzes();
};

// 刷新
const handleRefresh = () => {
    loadQuizzes();
};

const confirmPublish = async (record) => {
    try {
        publishLoading.value = true;
        const response = await getSingleQuiz(record.id);
        if (response && response.result) {
            publishQuizData.value = response.data;
            publishModalVisible.value = true;
        } else {
            showErrorMessage('載入測驗資料失敗');
        }
    } catch (error) {
        console.error('載入測驗資料失敗:', error);
        showErrorMessage('載入測驗資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        publishLoading.value = false;
    }
};

const handleConfirmPublish = async () => {
    if (!publishQuizData.value) return;

    try {
        publishLoading.value = true;
        const response = await updateQuizzes(publishQuizData.value.id, {
            status: 'published'
        });

        if (response.result) {
            message.success('發布成功');
            publishModalVisible.value = false;
            publishQuizData.value = null;
            loadQuizzes();
        } else {
            showErrorMessage(response.message || '發布失敗');
        }
    } catch (error) {
        const { response } = error;
        const { data } = response || {};
        const errorMessage = data?.message || error?.message || '發布失敗';
        showErrorMessage(errorMessage);
        console.error('發布測驗失敗:', error);
    } finally {
        publishLoading.value = false;
    }
};

const confirmArchive = async (record) => {
    try {
        archiveLoading.value = true;
        const response = await getSingleQuiz(record.id);
        if (response && response.result) {
            archiveQuizData.value = response.data;
            archiveModalVisible.value = true;
        } else {
            showErrorMessage('載入測驗資料失敗');
        }
    } catch (error) {
        console.error('載入測驗資料失敗:', error);
        showErrorMessage('載入測驗資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        archiveLoading.value = false;
    }
};

const handleConfirmArchive = async () => {
    if (!archiveQuizData.value) return;

    try {
        archiveLoading.value = true;
        const response = await updateQuizzes(archiveQuizData.value.id, {
            status: 'archived'
        });

        if (response.result) {
            message.success('封存成功');
            archiveModalVisible.value = false;
            archiveQuizData.value = null;
            loadQuizzes();
        } else {
            showErrorMessage(response.message || '封存失敗');
        }
    } catch (error) {
        const { response } = error;
        const { data } = response || {};
        const errorMessage = data?.message || error?.message || '封存失敗';
        showErrorMessage(errorMessage);
        console.error('封存測驗失敗:', error);
    } finally {
        archiveLoading.value = false;
    }
};

const confirmDelete = async (record) => {
    try {
        deleteLoading.value = true;
        const response = await getSingleQuiz(record.id);
        if (response && response.result) {
            deleteQuizData.value = response.data;
            deleteModalVisible.value = true;
        } else {
            showErrorMessage('載入測驗資料失敗');
        }
    } catch (error) {
        console.error('載入測驗資料失敗:', error);
        showErrorMessage('載入測驗資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        deleteLoading.value = false;
    }
};

const handleConfirmDelete = async () => {
    if (!deleteQuizData.value) return;

    try {
        deleteLoading.value = true;
        const response = await deleteQuizzes(deleteQuizData.value.id);

        if (response.result) {
            message.success('刪除成功');
            deleteModalVisible.value = false;
            deleteQuizData.value = null;
            loadQuizzes();
        } else {
            showErrorMessage(response.message || '刪除失敗');
        }
    } catch (error) {
        const { response } = error;
        const { data } = response || {};
        const errorMessage = data?.message || error?.message || '刪除失敗';
        showErrorMessage(errorMessage);
        console.error('刪除測驗失敗:', error);
    } finally {
        deleteLoading.value = false;
    }
};

const goCreate = () => {
    router.push({
        name: 'QuizzesCreate'
    });
};

const goEdit = (id) => {
    router.push({
        name: 'QuizzesEdit',
        params: { id }
    });
};

const goSetAnswers = (id) => {
    router.push({
        name: 'QuizzesSetAnswers',
        params: { id }
    });
};

const goView = (id) => {
    router.push({
        name: 'QuizzesView',
        params: { id }
    });
};

// 取得狀態顏色
const getStatusColor = (status) => {
    const colorMap = {
        draft: 'orange',
        published: 'green',
        archived: 'default'
    };
    return colorMap[status] || 'default';
};

// 取得模式顏色
// 獲取計分模式顏色（Morandi 色系）
const getScoringModeColor = (scoringMode) => {
    // Morandi 色系：莫蘭迪藍、莫蘭迪黃、莫蘭迪綠
    // 同時支援英文和中文格式
    const colorMap = {
        normal: '#3b6ea5', // Morandi 藍（測驗）
        '測驗': '#3b6ea5',
        dimensions: '#B8A05A', // Morandi 黃（多維）
        '多維': '#B8A05A',
        '維度': '#B8A05A',
        survey: '#9BB7D4', // Morandi 綠（問卷）
        '問卷': '#9BB7D4'
    };
    return colorMap[scoringMode] || 'default';
};

const getModeColor = (mode) => {
    return getScoringModeColor(mode);
};

onMounted(() => {
    // 先初始化可見欄位，再載入設置
    initVisibleColumns();
    loadColumnSettings();
    loadQuizzes();
});
</script>

<style scoped>
.points-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    padding: 32px;
    min-height: 500px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
    transition: all 0.3s ease;
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0.8;
    z-index: 0;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(200, 200, 210, 0.4);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.search-box {
    flex: 0 0 auto;
}

/* 搜尋框樣式見 @/styles/page-layout.css（search-input-field） */

/* 下拉選單樣式 */
.status-select :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-selection-item),
.status-select :deep(.ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.quiz_answer_mask {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;
    width: 24px;
    height: 24px;
    background-color: #87d068 !important;
    border-radius: 4px;
    color: #333;
    font-size: 12px;
}

.quiz-name-link {
    color: #2a4a7a !important;
    cursor: pointer;
    transition: all 0.3s ease;
}

.quiz-name-link:hover {
    color: #64748b !important;
    text-shadow: 0 0 10px rgba(255, 158, 207, 0.5);
}

/* 兩行顯示單元格樣式 */
:deep(.two-line-cell) {
    display: flex !important;
    flex-direction: column !important;
    gap: 3px !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    min-height: 40px !important;
    justify-content: center !important;
}

:deep(.cell-line) {
    display: flex !important;
    align-items: center !important;
    white-space: nowrap !important;
    font-size: 12px !important;
}

.two-line-cell {
    display: flex !important;
    flex-direction: column !important;
    gap: 3px !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    min-height: 40px !important;
    justify-content: center !important;
}

.cell-line {
    display: flex !important;
    align-items: center !important;
    white-space: nowrap !important;
    font-size: 12px !important;
}

.cell-label {
    font-weight: 600;
    color: rgba(42, 42, 58, 0.7);
    margin-right: 4px;
    font-size: 11px;
    min-width: 36px;
}

/* 表格樣式 */
:deep(.ant-table-thead > tr > th) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-table) {
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(42, 74, 122, 0.1) !important;
}

/* 確保表格單元格內的字體大小 */
:deep(.ant-table-cell .two-line-cell),
:deep(.ant-table-tbody .two-line-cell) {
    font-size: 12px !important;
}

:deep(.ant-table-cell .cell-line),
:deep(.ant-table-tbody .cell-line) {
    font-size: 12px !important;
}

/* 確保固定欄位正確顯示 */
:deep(.ant-table-cell-fix-right) {
    background: #fff !important;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05) !important;
}

:deep(.ant-table-cell-fix-right-first::after) {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.08) !important;
}

/* 移除固定欄位與前一欄位的間隔 */
:deep(.ant-table-cell-fix-right-first) {
    border-left: none !important;
}

:deep(.ant-table-cell) {
    padding-left: 8px !important;
    padding-right: 8px !important;
}

/* 操作欄位允許換行 - 覆蓋所有可能的 nowrap 設定 */
.points-table :deep(.ant-table-cell:has(.action-buttons)),
.points-table :deep(.ant-table-tbody .ant-table-cell:has(.action-buttons)),
.points-table :deep(.ant-table-tbody td:has(.action-buttons)) {
    white-space: normal !important;
}

/* 覆蓋 custom-table 的 nowrap 設定 */
.points-table.custom-table :deep(.ant-table-cell .action-buttons .ant-space),
.points-table :deep(.custom-table .ant-table-cell .action-buttons .ant-space) {
    flex-wrap: wrap !important;
}

/* 當表格不需要橫向滾動時，移除固定欄位的間隔 */
:deep(.ant-table-body) {
    overflow-x: auto !important;
}

:deep(.ant-table-container) {
    overflow-x: auto !important;
}

/* DataTable component integration - 與會員列表統一 */
:deep(.ant-card) {
    background: transparent !important;
    border: none !important;
}

:deep(.ant-card-body) {
    padding: 0 !important;
}

/* 操作按鈕樣式 - 覆蓋 custom-table 的 nowrap，強制兩個一行 */
.points-table :deep(.ant-table-cell .action-buttons),
.points-table :deep(.ant-table-tbody .ant-table-cell .action-buttons),
.points-table :deep(.ant-table-tbody td .action-buttons) {
    white-space: normal !important;
}

.points-table :deep(.ant-table-cell .action-buttons .ant-space),
.points-table
    :deep(.ant-table-tbody .ant-table-cell .action-buttons .ant-space),
.points-table :deep(.ant-table-tbody td .action-buttons .ant-space),
.action-buttons :deep(.ant-space) {
    display: grid !important;
    grid-template-columns: repeat(2, max-content) !important;
    gap: 4px !important;
    width: fit-content !important;
    flex-wrap: wrap !important;
    align-items: start !important;
}

.points-table :deep(.ant-table-cell .action-buttons .ant-space-item),
.points-table
    :deep(.ant-table-tbody .ant-table-cell .action-buttons .ant-space-item),
.points-table :deep(.ant-table-tbody td .action-buttons .ant-space-item),
.action-buttons :deep(.ant-space-item) {
    flex-shrink: 0 !important;
    width: auto !important;
    max-width: none !important;
}

.points-table :deep(.ant-table-cell .action-buttons .ant-space-item .ant-btn),
.points-table
    :deep(
        .ant-table-tbody
            .ant-table-cell
            .action-buttons
            .ant-space-item
            .ant-btn
    ),
.action-buttons :deep(.ant-space-item .ant-btn) {
    width: auto !important;
    min-width: auto !important;
    max-width: none !important;
}

/* 其他按鈕樣式 */
:deep(.ant-btn) {
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-btn .anticon) {
    color: inherit !important;
}

/* 欄位設置樣式 */
.column-settings-content {
    padding: 8px 0;
}

.column-settings-hint {
    margin: 0 0 20px 0;
    color: rgba(42, 42, 58, 0.7);
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

.column-checkbox-group {
    width: 100%;
}

.column-checkbox-group :deep(.ant-checkbox-wrapper) {
    margin-bottom: 8px;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
}

.column-checkbox-group :deep(.ant-checkbox-wrapper:hover) {
    color: #2a4a7a;
}

.column-checkbox-group :deep(.ant-checkbox-checked .ant-checkbox-inner) {
    background-color: #2a4a7a;
    border-color: #2a4a7a;
}

.column-checkbox-group :deep(.ant-checkbox:hover .ant-checkbox-inner) {
    border-color: #2a4a7a;
}

.column-checkbox-group :deep(.ant-checkbox-disabled) {
    cursor: not-allowed;
    opacity: 0.6;
}

.required-badge {
    margin-left: 4px;
    font-size: 11px;
    color: rgba(42, 42, 58, 0.6);
    font-style: italic;
}

/* 測驗 Modal 樣式 */
.quiz-modal-content {
    font-family: 'Courier New', monospace;
}

.quiz-info {
    margin-bottom: 24px;
}

.quiz-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.quiz-name-display {
    font-size: 15px;
    font-weight: 600;
    color: #2a2a3a;
}

.quiz-action-hint {
    margin-top: 20px;
}

.quiz-modal-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.quiz-modal-content :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.quiz-modal-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

/* 計分模式 tag 樣式（統一） */
.scoring-mode-tag {
    border: none !important;
    font-family: 'Courier New', monospace !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    padding: 2px 8px !important;
    height: 20px !important;
    line-height: 16px !important;
    border-radius: 4px !important;
    margin-right: 8px !important;
}

/* 測驗說明欄：限制行數，避免列高被拉長 */
.quiz-description-cell {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: 1.4;
    word-break: break-word;
}
</style>
